import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import ConversationPanel from './components/ConversationPanel';
import MessageInput from './components/MessageInput';
import ConceptCard from './components/ConceptCard';
import ConversationHistory from './components/ConversationHistory';
import QuickPrompts from './components/QuickPrompts';
import { analyzeAppRequirements } from '../../services/aiService';

const AIAppGenerator = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'Hi! I\'m your AI app generator assistant. Describe the app you want to build, and I\'ll help you create a structured concept with features, components, and technology recommendations.',
      timestamp: new Date(),
    },
  ]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentConcept, setCurrentConcept] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [savedConcepts, setSavedConcepts] = useState([]);
  const conversationEndRef = useRef(null);

  useEffect(() => {
    // Load saved conversations and concepts from localStorage
    const saved = localStorage.getItem('ai_app_concepts');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedConcepts(parsed);
      } catch (error) {
        console.error('Error loading saved concepts:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    conversationEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (userMessage) => {
    if (!userMessage?.trim()) return;

    // Add user message to conversation
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsAnalyzing(true);

    try {
      // Analyze user requirements using AI service
      const analysis = await analyzeAppRequirements(userMessage);

      // Add AI response with analysis
      const aiMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Great! I've analyzed your requirements. Here's what I recommend:`,
        timestamp: new Date(),
        concept: analysis,
      };
      setMessages((prev) => [...prev, aiMsg]);
      setCurrentConcept(analysis);
    } catch (error) {
      const errorMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I encountered an error analyzing your requirements: ${error?.message}. Please try again or rephrase your description.`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleQuickPrompt = async (prompt) => {
    handleSendMessage(prompt);
  };

  const handleSaveConcept = () => {
    if (currentConcept) {
      const saved = {
        id: Date.now(),
        concept: currentConcept,
        timestamp: new Date(),
        conversation: messages,
      };
      const updated = [...savedConcepts, saved];
      setSavedConcepts(updated);
      localStorage.setItem('ai_app_concepts', JSON.stringify(updated));

      // Show success message
      const successMsg = {
        id: Date.now() + 1,
        type: 'assistant',
        content: '✅ Concept saved successfully! You can find it in your saved concepts.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, successMsg]);
    }
  };

  const handleProceedToBuilder = () => {
    if (currentConcept) {
      // Store the concept in session storage for the visual builder
      sessionStorage.setItem('ai_generated_concept', JSON.stringify(currentConcept));
      navigate('/visual-builder');
    }
  };

  const handleLoadConversation = (conversation) => {
    setMessages(conversation?.conversation);
    setCurrentConcept(conversation?.concept);
  };

  const handleNewConversation = () => {
    setMessages([
      {
        id: 1,
        type: 'assistant',
        content: 'Hi! I\'m your AI app generator assistant. Describe the app you want to build, and I\'ll help you create a structured concept with features, components, and technology recommendations.',
        timestamp: new Date(),
      },
    ]);
    setCurrentConcept(null);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header - FIXED: Proper z-index */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0 relative z-10">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/project-dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">AI App Generator</h1>
              <p className="text-sm text-gray-500">Describe your app, get instant code structure</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleNewConversation}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          New Conversation
        </button>
      </header>
      
      {/* Main Content - FIXED: Proper flex container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Conversation History - FIXED: Proper width */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-white">
          <ConversationHistory
            savedConcepts={savedConcepts}
            onLoadConversation={handleLoadConversation}
            onDeleteConcept={(id) => {
              const updated = savedConcepts?.filter((c) => c?.id !== id);
              setSavedConcepts(updated);
              localStorage.setItem('ai_app_concepts', JSON.stringify(updated));
            }}
          />
        </div>

        {/* Main Chat Area - FIXED: Proper flex grow */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Quick Prompts - FIXED: Conditional render */}
          {messages?.length === 1 && (
            <div className="flex-shrink-0">
              <QuickPrompts onSelectPrompt={handleQuickPrompt} />
            </div>
          )}

          {/* Conversation Panel - FIXED: Proper scroll container */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <ConversationPanel messages={messages} />
            {isAnalyzing && (
              <div className="flex items-center gap-2 text-purple-600 py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">Analyzing your requirements...</span>
              </div>
            )}
            <div ref={conversationEndRef} />
          </div>

          {/* Generated Concept Card - FIXED: Positioned properly */}
          {currentConcept && (
            <div className="px-6 pb-4 flex-shrink-0">
              <ConceptCard
                concept={currentConcept}
                onSave={handleSaveConcept}
                onProceedToBuilder={handleProceedToBuilder}
                onRegenerate={() => {
                  const lastUserMessage = messages?.filter((m) => m?.type === 'user')?.pop();
                  if (lastUserMessage) {
                    handleSendMessage(lastUserMessage?.content);
                  }
                }}
              />
            </div>
          )}

          {/* Message Input - FIXED: Sticky bottom */}
          <div className="border-t border-gray-200 bg-white px-6 py-4 flex-shrink-0">
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={isAnalyzing}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAppGenerator;